import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateShareholderLoanInput,
  RecordShareholderLoanPaymentInput,
  RecordShareholderLoanPaymentResult,
  ShareholderLoan,
  ShareholderLoanWithDetails,
} from '@accounting/shared';
import { api } from '../lib/api';

export function useShareholderLoans(companyId: string | null) {
  return useQuery({
    queryKey: ['shareholder-loans', companyId ?? 'none'],
    enabled: companyId !== null,
    queryFn: () =>
      api
        .get<{ loans: ShareholderLoan[] }>(`/companies/${companyId}/shareholder-loans`)
        .then((r) => r.loans),
  });
}

export function useShareholderLoan(companyId: string | null, id: string) {
  return useQuery({
    queryKey: ['shareholder-loan', companyId ?? 'none', id],
    enabled: companyId !== null && !!id,
    queryFn: () =>
      api
        .get<{ loan: ShareholderLoanWithDetails }>(`/companies/${companyId}/shareholder-loans/${id}`)
        .then((r) => r.loan),
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>, companyId: string | null) {
  const c = companyId ?? 'none';
  qc.invalidateQueries({ queryKey: ['shareholder-loans', c] });
  qc.invalidateQueries({ queryKey: ['shareholder-loan', c] });
  qc.invalidateQueries({ queryKey: ['journal-entries', c] });
  qc.invalidateQueries({ queryKey: ['accounts-balances', c] });
  qc.invalidateQueries({ queryKey: ['report'] });
}

export function useCreateShareholderLoan(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateShareholderLoanInput) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ loan: ShareholderLoanWithDetails }>(`/companies/${companyId}/shareholder-loans`, input)
        .then((r) => r.loan);
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}

export function useRecordShareholderLoanPayment(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: RecordShareholderLoanPaymentInput }) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ result: RecordShareholderLoanPaymentResult }>(
          `/companies/${companyId}/shareholder-loans/${id}/payments`,
          input,
        )
        .then((r) => r.result);
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}
